import React from 'react';
import { radii, textSize } from 'design/tokens';

/**
 * The dark hover tooltip shared by every small bar chart — the Spending history
 * strip, the merchant sparklines and the panel's spend-by-month chart.
 *
 * It sits inside the chart's own positioned container and is measured in
 * percentages of it, so the caller only has to say which bar of how many is
 * hovered. The first two bars anchor to their left edge and the last two to
 * their right, which is what keeps a tooltip on the end of a 12-bar strip from
 * hanging outside the panel.
 */
export const DarkTip: React.FC<{
  /** Index of the hovered bar. */
  index: number;
  /** How many bars the strip has, for the centring maths. */
  count: number;
  /** The headline, typically an amount. */
  amount: React.ReactNode;
  /** The dimmer line under it — a month, or "July · 3 transactions". */
  sub?: React.ReactNode;
}> = ({ index, count, amount, sub }) => {
  const transform =
    index < 2 ? 'none' : index > count - 3 ? 'translateX(-100%)' : 'translateX(-50%)';

  return (
    <span
      style={{
        position: 'absolute',
        zIndex: 6,
        pointerEvents: 'none',
        bottom: 'calc(100% + 6px)',
        left: `${(((index + 0.5) / Math.max(1, count)) * 100).toFixed(2)}%`,
        transform,
        background: 'var(--st-ink)',
        color: 'var(--st-card)',
        padding: '7px 10px',
        borderRadius: radii.sm,
        whiteSpace: 'nowrap',
        textAlign: 'center',
        boxShadow: '0 6px 18px rgba(0,0,0,.18)',
      }}
    >
      <span className="st-num" style={{ display: 'block', fontSize: textSize.body, fontWeight: 600 }}>
        {amount}
      </span>
      {sub != null && (
        <span style={{ display: 'block', fontSize: textSize.caption, opacity: 0.7, marginTop: 2 }}>{sub}</span>
      )}
    </span>
  );
};

export default DarkTip;
